import * as React from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography } from '@mui/material';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import RuleIcon from '@mui/icons-material/Rule';

import schemaJson from './finalthesis.schema.json';

import * as Data from './DataRepresentation';
import * as Form from './finalthesis';
import {T, useTolgee} from "@tolgee/react";
import {HTML_TAGS} from "./I18n";
import {JsonSchema7} from "@jsonforms/core";



// Define a type for the component props
interface HelpDialogProps {

	open: boolean,
	setOpen: React.Dispatch<React.SetStateAction<boolean>>,
	formData: Form.AnmeldungDerAbschlussarbeit
}

// Use the 'FC' (Functional Component) generic from React, with our props type
const HelpDialog: React.FC<HelpDialogProps> = ({ open, setOpen, formData }: HelpDialogProps) => {

	const {t} = useTolgee();

	const handleClose = () => {
		setOpen(false);
	};

	const descriptionElementRef = React.useRef<HTMLElement>(null);
	React.useEffect(() => {
		if (open) {
			const { current: descriptionElement } = descriptionElementRef;
			if (descriptionElement !== null) {
				descriptionElement.focus();
			}
		}
	}, [open]);

	const formKeys = [];

	const schema = schemaJson as JsonSchema7;
	
	for (const key in schema.properties) {
		const property = schema.properties[key];
		if (property === undefined || property.type === 'object') {
			continue;
		}
		formKeys.push(key);
	}

	const requirementsPropertyName = Data.getRequirementsPropertyName(formData);

	if (requirementsPropertyName !== undefined && schema.properties !== undefined) {
		const requirementsDefinition = schema.properties[requirementsPropertyName];
		if (requirementsDefinition) {
			for (const key in requirementsDefinition.properties) {
				formKeys.push(requirementsPropertyName + "." + key);
			}
		}
	}

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleClose}
				scroll={'paper'}
				aria-labelledby="help-dialog-title"
				aria-describedby="help-dialog-description"
			>
				<DialogTitle id="dialog-title">{t("page.dialog.help.title")}</DialogTitle>
				<DialogContent dividers={true}>
					<DialogContentText
						id="help-dialog-description"
						ref={descriptionElementRef}
						tabIndex={-1}
					>
						<T
							keyName="page.dialog.help.text"
							params={{
								...HTML_TAGS,
								link_icon: <LinkIcon aria-label={t("aria.link_icon")} style={{verticalAlign:'middle'}} />,
								download_icon: <FileDownloadIcon aria-label={t("aria.download_icon")} style={{verticalAlign:'middle'}} />,
								print_icon: <PrintIcon aria-label={t("aria.prt=int_icon")} style={{verticalAlign:'middle'}} />,
								validation_icon: <RuleIcon aria-label={t("aria.validation_icon")} style={{verticalAlign:'middle'}} />
							}}
						/>
						<Typography variant="h5">
							{t("page.dialog.help.descriptions.title")}
						</Typography>

						<TableContainer component={Paper}>
							<Table aria-label={t("aria.dialog.help.description_table")}>
								<TableHead>
									<TableRow>
										<TableCell>{t("page.dialog.help.column_field")}</TableCell>
										<TableCell>{t("page.dialog.help.column_description")}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
								{
									formKeys.map((key) =>
										(<TableRow>
										<TableCell>{t(key + ".label")}</TableCell>
										<TableCell>{t(key + ".description")}</TableCell>
										</TableRow>)
									)
								}
								</TableBody>
							</Table>
						</TableContainer>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>{t("page.dialog.ok")}</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}
export default HelpDialog;